import {
	PROGRAM_ISSUE_CAUGHT_PHASE_LABELS,
	PROGRAM_ISSUE_FAULT_LABELS,
	PROGRAM_ISSUE_SEVERITY_LABELS,
} from '../../interfaces/CamProgramming.interface';
import type {
	Program,
	ProgramIssue,
	ProgramIssueCaughtPhase,
	ProgramIssueFault,
	ProgramIssueSeverity,
	ProgramUser,
} from '../../interfaces/CamProgramming.interface';

/** Shared by the issues list and the copy staged inside the review dialog. */
export const SEVERITY_COLORS: Record<ProgramIssueSeverity, string> = {
	low: 'default',
	medium: 'gold',
	high: 'orange',
	critical: 'red',
	negligent: 'magenta',
};

export const SEVERITY_OPTIONS = (
	Object.keys(PROGRAM_ISSUE_SEVERITY_LABELS) as ProgramIssueSeverity[]
).map((value) => ({ label: PROGRAM_ISSUE_SEVERITY_LABELS[value], value }));

export const CAUGHT_PHASE_OPTIONS = (
	Object.keys(PROGRAM_ISSUE_CAUGHT_PHASE_LABELS) as ProgramIssueCaughtPhase[]
).map((value) => ({
	label: PROGRAM_ISSUE_CAUGHT_PHASE_LABELS[value],
	value,
}));

export const DEFAULT_SEVERITY: ProgramIssueSeverity = 'medium';
export const DEFAULT_CAUGHT_PHASE: ProgramIssueCaughtPhase = 'review';

/** Unless someone says otherwise, the program is the programmer's work. */
export const DEFAULT_FAULT: ProgramIssueFault = 'programmer';

const FAULT_ORDER: ProgramIssueFault[] = [
	'programmer',
	'first_reviewer',
	'secondary_reviewer',
];

const holderForFault = (
	program: Program | undefined,
	fault: ProgramIssueFault
): ProgramUser | null => {
	if (!program) return null;
	if (fault === 'programmer') return program.programmer;
	if (fault === 'first_reviewer') return program.first_reviewer;
	return program.secondary_reviewer;
};

/**
 * Fault options named after the people actually on the program, so the choice
 * reads as "blame Phong's edit" rather than an abstract role. A role nobody
 * holds is offered but disabled — selecting it would charge no one and quietly
 * make the issue free.
 */
export const faultOptions = (program?: Program) =>
	FAULT_ORDER.map((fault) => {
		const holder = holderForFault(program, fault);
		const name = holder?.full_name || holder?.email;
		return {
			value: fault,
			label: name
				? `${PROGRAM_ISSUE_FAULT_LABELS[fault]} — ${name}`
				: `${PROGRAM_ISSUE_FAULT_LABELS[fault]} (none logged)`,
			disabled: !!program && !holder,
		};
	});

/** Who the fault currently points at, for display outside the form. */
export const faultHolder = (
	program: Program | undefined,
	issue: Pick<ProgramIssue, 'fault'>
) => holderForFault(program, issue.fault);

/** First review → Review; secondary and later → Secondary Review. */
export const caughtPhaseForReviewSlot = (
	reviewIndex: number
): ProgramIssueCaughtPhase =>
	reviewIndex === 0 ? 'review' : 'secondary_review';

/** A screenshot on its own describes an issue, so text or an image will do. */
export const hasIssueDescription = (html: string | null | undefined) =>
	!!html && (!!html.replace(/<[^>]*>/g, '').trim() || html.includes('<img'));

/**
 * An issue as held by the review dialog, which cannot write it yet: a review
 * being logged for the first time has no id for the issue to hang off, so the
 * whole set is staged and reconciled once the review is saved.
 */
export interface ReviewIssueDraft {
	/** Stable handle for the list. Equals `id` for an issue already stored. */
	key: string;
	/** Only set once the issue exists on the server. */
	id?: string;
	severity: ProgramIssueSeverity;
	caught_phase: ProgramIssueCaughtPhase;
	fault: ProgramIssueFault;
	insufficient_training: boolean;
	description: string;
}

/**
 * Whether a staged issue is worth writing. Opening the composer and typing
 * nothing leaves a row holding only its default severity, which is not an
 * issue — it is a click. Anything already stored is kept regardless, so a blank
 * row written before descriptions were required is not swept away.
 */
export const isIssueWorthSaving = (issue: ReviewIssueDraft) =>
	!!issue.id || hasIssueDescription(issue.description);

export const issueToDraft = (issue: ProgramIssue): ReviewIssueDraft => ({
	key: issue.id,
	id: issue.id,
	severity: issue.severity,
	caught_phase: issue.caught_phase,
	fault: issue.fault,
	insufficient_training: issue.insufficient_training,
	description: issue.description || '',
});
